import React, { useEffect, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────
// EarningsCountdown — next earnings report of a company (companies_directory
// earnings_date YYYY-MM-DD + earnings_time bmo|amc|"") with a live countdown.
// The target instant is New York time: before-open → 08:00, after-close →
// 16:00, unconfirmed → 00:00 of that day. Past dates render nothing, so a
// stale date never shows a negative timer. Colors come from the theme tokens
// passed by the host page (Moderno / default), never hardcoded per brand.
// ─────────────────────────────────────────────────────────────────────────

const TZ = 'America/New_York';
const HOUR = { bmo: 8, amc: 16 };
const TIMING = { bmo: 'Before market open', amc: 'After market close' };

// Offset (ms) of New York vs UTC at a given instant (handles EST/EDT).
function nyOffset(at) {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at).map(x => [x.type, x.value]));
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - at.getTime();
}

export function earningsTarget(date, time) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '');
  if (!m) return null;
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], HOUR[time] || 0);
  return wall - nyOffset(new Date(wall));
}

function split(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return [
    ['Days', Math.floor(s / 86400)], ['Hours', Math.floor(s / 3600) % 24],
    ['Min', Math.floor(s / 60) % 60], ['Sec', s % 60],
  ];
}

export default function EarningsCountdown({ date, time, heading, ink, muted, accent, line, radius = 3, font }) {
  const target = earningsTarget(date, time);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target || target <= Date.now()) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);

  if (!target || target <= now) return null;

  const label = new Date(target).toLocaleDateString('en-US', {
    timeZone: TZ, weekday: 'short', year: 'numeric', month: 'long', day: 'numeric',
  });
  const parts = split(target - now);
  const soon = target - now < 7 * 86400000;

  return (
    <section className="px-6 py-5 border" style={{ borderColor: line, borderRadius: radius }} aria-label="Next earnings report" data-testid="company-earnings">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.14em', color: muted }}>Next earnings</span>
        {soon && <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: accent }}>This week</span>}
      </div>
      <div className="mt-2 text-[17px] font-semibold" style={{ color: ink, fontFamily: font }} data-testid="company-earnings-date">{label}</div>
      <div className="text-[13px] mt-0.5" style={{ color: muted }}>{TIMING[time] || 'Time not confirmed'}</div>
      <div className="grid grid-cols-4 gap-2 mt-4" role="timer" aria-live="off" data-testid="company-earnings-countdown">
        {parts.map(([k, v]) => (
          <div key={k} className="flex flex-col items-center py-2.5 border" style={{ borderColor: line, borderRadius: radius }}>
            <span className="text-[22px] font-bold leading-none" style={{ color: heading, fontVariantNumeric: 'tabular-nums', fontFamily: font }}>{String(v).padStart(2, '0')}</span>
            <span className="text-[10px] font-semibold uppercase mt-1.5" style={{ letterSpacing: '.1em', color: muted }}>{k}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
